import { keepPreviousData } from "@tanstack/react-query";
import { useAuthQuery, UseQueryExtendedOptions } from "@rentablo/api";
import {
  CashFlowAndPerformanceStatisticsList,
  performanceByDepots,
} from "@rentablo/api/apiv1";
import { ACCOUNT_PERFORMANCE_BY_ACCOUNT_IDS } from "@api/cacheKeys";

export interface Attrs {
  accountIds?: number[];
  startDate?: Date;
  endDate?: Date;
  interval?: "day" | "week" | "month" | "year";
  displayCurrency?: string;
}

export const usePerformanceByAccounts = (
  { accountIds, startDate, endDate, interval = "day", displayCurrency }: Attrs,
  options: UseQueryExtendedOptions<
    CashFlowAndPerformanceStatisticsList,
    AxiosApiError,
    CashFlowAndPerformanceStatisticsList
  > = {}
) => {
  const query = useAuthQuery<
    CashFlowAndPerformanceStatisticsList,
    AxiosApiError,
    CashFlowAndPerformanceStatisticsList
  >(
    [
      ACCOUNT_PERFORMANCE_BY_ACCOUNT_IDS,
      accountIds?.sort(),
      startDate?.getTime(),
      endDate?.getTime(),
      interval,
      displayCurrency,
    ],

    ({ token }) =>
      performanceByDepots(token, {
        createCashFlowAndPerformanceStatisticsParams: {
          accountIds,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          interval,
          displayCurrency,
          cashFlowAndPerformanceStatisticsParamsList: [
            { assetClasses: [], includeCash: false, includeDividends: true },
            { assetClasses: [], includeCash: true, includeDividends: true },
          ],
        },
      }),
    {
      enabled: !!accountIds && accountIds.length > 0,
      placeholderData: keepPreviousData,
      ...options,
    }
  );

  const [withoutCash, withCash] =
    query.data?.cashFlowAndPerformanceStatisticsList || [];
  const data = {
    withoutCash,
    withCash,
  };
  return { ...query, data };
};
